import { useQuery } from '@apollo/client';
import { DateCell } from '@openx/components-administration/lib/UsersList/DateCell.tsx';
import { NameCell } from '@openx/components-organization/lib/components/OrganizationsList/components/NameCell.tsx';
import { getAllowedCountriesLabels } from '@openx/components-organization/lib/constants.ts';
import type {
  OrganizationRowActions,
  UseGetOrganizationsTableData,
} from '@openx/components-organization/lib/utils/context/OrganizationHookTypes.ts';
import type { OrganizationTreeRowData } from '@openx/types/organization.ts';
import type { Columns } from '@openx/types/tableHelpers.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { GetOrganizationsListDocument } from '../../../graphql/query/organizations/GetOrganizationsList.generated.tsx';

export const useGetOrganizationsTableData: UseGetOrganizationsTableData = ({ organizations, searchPhrase }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const mapOfAllowedCountries = getAllowedCountriesLabels(t);

  const parentOrganizationsIds = useMemo(() => {
    if (!searchPhrase) return [];

    const presentIds = new Set(organizations.map(org => org.id));

    return [...new Set(organizations.flatMap(org => org.path_to_root ?? []))].filter(id => !presentIds.has(id));
  }, [searchPhrase, organizations]);

  const { data: parentOrganizationsData } = useQuery(GetOrganizationsListDocument, {
    variables: { where: { id: { _in: parentOrganizationsIds } } },
    skip: !searchPhrase || parentOrganizationsIds.length === 0,
    fetchPolicy: 'no-cache',
  });

  const fetchedParentOrganizations = parentOrganizationsData?.account ?? [];

  const parentOrganizations = useMemo(
    () => (searchPhrase ? [...organizations, ...fetchedParentOrganizations] : []),
    [searchPhrase, organizations, fetchedParentOrganizations]
  );

  const rowActions: OrganizationRowActions = useMemo(
    () => ({
      CREATE: {
        color: 'primary',
        'data-test': 'create-sub-organization-action-button',
        onClick: ({ id }) => {
          navigate(`/organizations/create`, { state: { selectedParentId: id } });
        },
        label: t('Create Sub Organization'),
      },
      DETAILS: {
        'data-test': 'details-action-button',
        label: t('Details'),
        onClick: ({ id }) => {
          navigate(`/organizations/${id}/details`);
        },
      },
      EDIT: {
        'data-test': 'edit-action-button',
        label: t('Edit'),
        onClick: ({ id }) => {
          navigate(`/organizations/${id}/edit`);
        },
      },
    }),
    [t, navigate]
  );

  const tableColumns: Columns<OrganizationTreeRowData> = useMemo(
    () => [
      {
        ellipsis: true,
        ellipsisMaxWidth: '650px',
        key: 'name',
        render: organization => (
          <NameCell organization={organization} parentOrganizations={parentOrganizations} searchPhrase={searchPhrase} />
        ),
        title: t('Name'),
        width: '40%',
        disabled: true,
      },
      {
        key: 'organization_type',
        title: t('Organization Type'),
        width: '15%',
      },
      {
        key: 'parent_account',
        render: ({ parent_account }) => (parent_account ? parent_account.name : null),
        title: t('Parent Organization'),
        width: '15%',
      },
      {
        key: 'allowed_countries',
        render: ({ allowed_countries = [] }) =>
          allowed_countries.map((country: string) => mapOfAllowedCountries[country] || country).join(', '),
        title: t('Allowed Countries'),
        width: '20%',
      },
      {
        isDate: true,
        key: 'created_date',
        render: ({ created_date }) => <DateCell date={created_date} />,
        title: t('Date Joined'),
        width: '10%',
      },
    ],
    [mapOfAllowedCountries, parentOrganizations, searchPhrase, t]
  );

  return {
    rowActions,
    tableColumns,
  };
};
